import { CORE_AXIOS, Http } from "@/core"
import { container } from "tsyringe"
import { computed } from 'vue'
import { Upload, message } from "ant-design-vue"
/**
 * 将图片地址做转换
 * 本地资源添加baseURL
 * @param resource
 * @returns
 */
export const toUrl = (resource: string | undefined): string | undefined => {
  if (resource) {
    if (resource.indexOf('http') === 0 || resource.indexOf('data:') === 0) {
      return resource
    } else {
      const axios = container.resolve<Http>(CORE_AXIOS)
      // 移除 resource 开头多余的斜杠
      const normalizedResource = resource.replace(/^\//, '')
      return `${axios.options.baseURL}${normalizedResource}`
    }
  }
  return resource
}

/**
 *  确认图片名是否携带中文 图片格式
 * 若携带中文则去除或用当前时间的时间戳命名
 * @param file
 * @returns
 */
export const verifyUploadImgName = (file: any) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
  if (isJpgOrPng) {
    let splitImgName = ''
    if (file.name.includes('.png'))
      splitImgName = file.name.split('.png')[0]
    else if (file.name.includes('.jpg'))
      splitImgName = file.name.split('.jpg')[0]
    const isValidImgName = !/^[a-zA-Z0-9]+$/.test(splitImgName)
    if (isValidImgName) {
      let newName = `${splitImgName.replace(/[^\w]|[\u4e00-\u9fa5]/g, '')}`
      if (newName.length === 0)
        newName = `${new Date().getTime()}.png`
      else
        newName += '.png'
      return new File([file], newName, { type: file.type })
    } else
      return new File([file], file.name, { type: file.type })
  } else {
    message.error('图片格式必须为jpg或png')
    return Upload.LIST_IGNORE
  }
}

/**
 *  部分用户名为手机号码 需要正则匹配 隐藏号码
 * @param userName
 * @returns
 */
export const hideMobileNumber = (userName: string) => {
  const wxPhoneRegex = /^wx(13[0-9]|14[0-9]|15[0-9]|16[0-9]|17[0-9]|18[0-9]|19[0-9])(\d{4})\d{4}$/
  const phoneRegex = /^(13[0-9]|14[0-9]|15[0-9]|16[0-9]|17[0-9]|18[0-9]|19[0-9])(\d{4})\d{4}$/
  if (wxPhoneRegex.test(userName) && userName.length === 13) {
    return userName.replace(wxPhoneRegex, (match, p1) => {
      return 'wx' + p1 + '****' + match.slice(-4)
    })
  } else if (phoneRegex.test(userName) && userName.length === 11) {
    return userName.replace(phoneRegex, (match, p1) => {
      return p1 + '****' + match.slice(-4)
    })
  }
  return userName
}

/**
 * 把图片资源转换为水印图资源
 * 规则为 第六个/后的编号 以-分割 0和2调换 1和3调换
 * @param resource
 * @returns
 */
export const toWatermark = (resource: string): string => {
  const originalLink = resource.split("/")
  const [part0, part1, part2, part3, part4] = originalLink[6].split('-')
  originalLink[6] = `${part2}-${part3}-${part0}-${part1}-${part4}`
  originalLink.splice(4, 0, 'wm')
  return `${originalLink.join("/")}`
}

/**
 * 把blob转成base64
 * @param blob
 * @returns
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.result) {
        // 结果是 data:URL, 只要 base64 部分取 1
        const base64String = (reader.result as string).split(',')[1]
        resolve(base64String)
      } else {
        reject(new Error('Could not convert blob to base64'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * 将html富文本转换成字符串
 * @param string
 * @returns
 */
export const getSimpleText = (html: string) => {
  if (html == null) return
  const refHtml = /<[^<>]+>/g
  return html.replace(refHtml, '')
}

export type TreeItem = {
  id: string
  parentId?: string
  parent_id?: string
  children?: TreeItem[]
}

/**
 * 数组 到 Tree 的转换
 * @param items
 * @returns
 */
export const convertArrayToTree = <T extends TreeItem>(items: T[]) => {
  type TMap = { [x: string]: T }
  type IndexMap = { [x: string]: number }
  const [map, tree, nodeMap] = [{} as IndexMap, [] as T[], {} as TMap, {} as TMap]

  items.forEach((item, i) => {
    const node = item
    node.children = []
    map[node.id] = i
    nodeMap[node.id] = node
  })

  for (let i = 0; i < items.length; i += 1) {
    const node = items[i]
    const parentId = node.parentId || node.parent_id
    if (parentId) {
      items[map[parentId]]?.children?.push(node)
    } else {
      tree.push(node)
    }
  }

  return {
    tree,
    idMap: nodeMap,
    items
  }
}

/**
 * 将数字转换成折扣
 * @param number
 * @returns
 */

export const numToDiscount = (num: number): string => {
  if (num == 100)
    return '原价'
  const numStr = num.toString() // 将数字转换成字符串
  const numArr = numStr.split('') // 将字符串转换成数组
  if (numArr[1] == '0') {
    return numArr[0] + '折'
  } else if (!numArr[1]) {
    return `0.${numArr[0]}折`
  } else {
    return num + '折'
  }
}

export const sortBy = (attr: any, rev: any) => {
  // 第二个参数没有传递 默认升序排列
  if (rev == undefined) {
    rev = 1
  } else {
    rev = (rev) ? 1 : -1
  }

  return function (a: any, b: any) {
    a = a[attr]
    b = b[attr]
    if (a < b) {
      return rev * -1
    }
    if (a > b) {
      return rev * 1
    }
    return 0
  }
}

export const renderImageBaseUrl = (record: any) => {
  const baseHeightRatio = Math.round(record.spu_custom_height * (200 / record.spu_custom_width))
  return toUrl(record.spu_customable_image) + `?x-oss-process=image/resize,h_${baseHeightRatio},m_lfit`
}

export const assignCustomizedData = computed(() => (custom_areas: any, bgWidth: number, customable_image_scale: number, boundingRect: any) => {
  return Object.assign({},
    { ...(custom_areas || {}) },
    { scale: (200 / bgWidth) * (customable_image_scale || custom_areas.scale) },
    boundingRect || {})
})

export const templateBasicParameters = (image: string, color: string, width: number, height: number, cornerSize: number = 15) => {
  return Object.assign({}, { image, color, width, height, cornerSize })
}

export const convertColor = (strColor: any): any | undefined => {
  if (typeof strColor !== 'string') {
    return strColor
  }

  try {
    return JSON.parse(strColor)
  } catch (e) {
    return { name: '', value: strColor }
  }
}
